/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

export type ViewMode = 'kanban' | 'table';

/** Which mobile Kanban presentation to render: status tabs, stacked accordion, or swipe carousel. */
export type MobileKanbanLayout = 'tabs' | 'stack' | 'carousel';

const MOBILE_KANBAN_LAYOUT_KEY = 'housien:visits-mobile-kanban-layout';
const MOBILE_KANBAN_LAYOUTS: MobileKanbanLayout[] = ['tabs', 'stack', 'carousel'];

/**
 * Reads the persisted mobile Kanban layout preference, defaulting to 'tabs'.
 *
 * @returns {MobileKanbanLayout} The initial mobile Kanban layout.
 */
export function getInitialMobileKanbanLayout(): MobileKanbanLayout {
  try {
    const stored = localStorage.getItem(MOBILE_KANBAN_LAYOUT_KEY);
    if (stored && (MOBILE_KANBAN_LAYOUTS as string[]).includes(stored)) {
      return stored as MobileKanbanLayout;
    }
  } catch { /* localStorage unavailable */ }
  return 'tabs';
}

/**
 * Persists the mobile Kanban layout preference.
 *
 * @param {MobileKanbanLayout} layout The layout to persist.
 */
export function persistMobileKanbanLayout(layout: MobileKanbanLayout): void {
  try {
    localStorage.setItem(MOBILE_KANBAN_LAYOUT_KEY, layout);
  } catch { /* localStorage unavailable */ }
}

/**
 * Compute the inclusive start/end date strings (YYYY-MM-DD) for a named period.
 * Supports "today", "thisWeek" (Mon-Sun) and falls back to the current month.
 */
export function getPeriodDates(period: string): { startDate?: string; endDate?: string } {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (period === 'today') {
    const s = fmt(now);
    return { startDate: s, endDate: s };
  }
  if (period === 'thisWeek') {
    const day = now.getDay();
    const mon = new Date(now);
    mon.setDate(now.getDate() - ((day + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { startDate: fmt(mon), endDate: fmt(sun) };
  }
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { startDate: fmt(first), endDate: fmt(last) };
}
